/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, forwardRef, Inject, Input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdditionalParams } from '@irian-ro/abstract-resource';
import { SelectableResourceService } from '@well/common-libs/selectable-resource-management/util';
import { map, Subject, switchMap, startWith } from 'rxjs';

@Component({
  selector: 'well-resource-id-dropdown',
  templateUrl: './resource-id-dropdown.component.html',
  styleUrls: ['./resource-id-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResourceIdDropdownComponent),
      multi: true
    }
  ]
})
export class ResourceIdDropdownComponent implements OnInit, ControlValueAccessor {
  @Input() resourceType: (new () => any) | undefined = undefined;
  @Input() placeholder: string | null | undefined = null;
  @Input() idField = 'uuid';
  @Input() labelField = 'label';
  @Input() disabled = false;
  @Input() additionalParams:  Array<AdditionalParams> | undefined = undefined;

  private searchQuery$ = new Subject<string>();
  resources: Signal<any[]>;
  selectedResource: any | undefined = undefined;
  resourceService: SelectableResourceService<any> | undefined = undefined;
  private onTouchedCallback: ((_: any) => void) | undefined = undefined;
  private onChangeCallback: ((_: any) => void) | undefined = undefined;
  @Input() label: string | null | undefined = null;

  constructor(@Inject(SelectableResourceService) private selectableResourceServices: SelectableResourceService<any>[]) {
    this.resources = toSignal(
      this.searchQuery$.pipe(
        startWith(''),
        switchMap(query => {
          if (!this.resourceService) {
            return [];
          }
          const filters: {[key:string]: object} = {};
          filters[this.labelField] = {
            value: query,
            matchMode: 'contains'
          };
          return this.resourceService.findAll({ filters }, this.additionalParams).pipe(
            map(resources => resources.data)
          );
        })
      ),
      { initialValue: [] }
    );
  }

  ngOnInit() {
    this.resourceService = this.selectableResourceServices.find(selectableResourceService => {
      return selectableResourceService.getType() === this.resourceType;
    });
  }

  writeValue(id: string): void {
    if (id) {
      this.resourceService?.findOne(id).subscribe((resource: any) => {
        this.selectedResource = {...resource.data};
      });
    }
    else {
      this.selectedResource = null;
    }
  }

  onChange() {
    if (this.onChangeCallback && this.selectedResource) {
      this.onChangeCallback(this.selectedResource[this.idField]);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onClear() {
    this.selectedResource = null;
    if (this.onChangeCallback) {
      this.onChangeCallback(null);
    }
  }

  onSelect() {
    this.onChange();
  }

  onBlur() {
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedResource);
    }
  }

  onQueryChange(query: string) {
    this.searchQuery$.next(query);
  }

  getDisplayLabel = (item: any): string => {
    return item?.[this.labelField] ?? '';
  };

  compareByIdField = (a: any, b: any): boolean => {
    return a && b && a[this.idField] === b[this.idField];
  };
}
